import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { mockLoginRequestSchema, type MockLoginRequest } from "@portal/schemas";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@portal/ui";
import { useForm } from "react-hook-form";
import { ApiError, mockLogin } from "../../api/client";
import { meQueryKey, useAuthConfig } from "../../api/queries";
import { LogIn } from "lucide-react";

const ERROR_MESSAGES: Record<string, string> = {
  DOMAIN_NOT_ALLOWED: "zenk.co.jp のアカウントでログインしてください。",
  NOT_REGISTERED: "このアカウントはポータルに登録されていません。管理者に登録を依頼してください。",
  NOT_ALLOWED: "このアカウントは現在利用できません。",
  ACCOUNT_MISMATCH: "別の Google アカウントに紐付いています。管理者に確認してください。",
  STATE_MISMATCH: "ログインの検証に失敗しました。もう一度お試しください。",
};

export function LoginPage() {
  const search = useSearch({ from: "/login" });
  const config = useAuthConfig();
  const redirectError = search.error
    ? (ERROR_MESSAGES[search.error] ?? "ログインに失敗しました。")
    : null;

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>ゼンク社内ポータル</CardTitle>
          <CardDescription>Google Workspace のアカウントでログインします。</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {redirectError && (
            <p role="alert" className="text-sm text-destructive">
              {redirectError}
            </p>
          )}
          {config.isPending && <p className="text-sm text-muted-foreground">読み込み中…</p>}
          {config.isError && (
            <p role="alert" className="text-sm text-destructive">
              サーバーに接続できません。
            </p>
          )}
          {config.data?.provider === "google" && (
            <Button asChild className="w-full">
              <a href="/api/auth/login">
                <LogIn />
                Google でログイン
              </a>
            </Button>
          )}
          {config.data?.provider === "mock" && <MockLoginForm />}
        </CardContent>
      </Card>
    </main>
  );
}

/** 開発環境専用。メールアドレスだけでログインする。 */
function MockLoginForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useForm<MockLoginRequest>({
    resolver: zodResolver(mockLoginRequestSchema),
    defaultValues: { email: "" },
  });
  const login = useMutation({
    mutationFn: mockLogin,
    onSuccess: async (data) => {
      queryClient.setQueryData(meQueryKey, data);
      await navigate({ to: "/" });
    },
  });
  const serverError =
    login.error instanceof ApiError
      ? login.error.message
      : login.error
        ? "ログインに失敗しました。"
        : null;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={form.handleSubmit((values) => login.mutate(values))}
      noValidate
    >
      <p className="text-xs text-muted-foreground">開発用モックログイン(AUTH_PROVIDER=mock)</p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="email">メールアドレス</Label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          placeholder="you@zenk.co.jp"
          {...form.register("email")}
        />
        {form.formState.errors.email && (
          <p className="text-xs text-destructive">メールアドレスの形式が正しくありません。</p>
        )}
      </div>
      {serverError && (
        <p role="alert" className="text-sm text-destructive">
          {serverError}
        </p>
      )}
      <Button type="submit" disabled={login.isPending}>
        <LogIn />
        {login.isPending ? "ログイン中…" : "ログイン"}
      </Button>
    </form>
  );
}
